import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * ExportCodeDialog - Dialog for displaying and copying exported deck code
 * shadcn/ui version
 */
export default function ExportCodeDialogShadcn({
  open,
  onOpenChange,
  exportCode,
}) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(exportCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('controls.exportDeckCode', 'Export Deck Code')}
          </DialogTitle>
          <DialogDescription>
            {t('deck.export.instructions', 'Copy this code to share your deck or import it later.')}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-3">
          <textarea
            className="w-full min-h-[100px] rounded-md border border-input bg-muted/50 px-3 py-2 text-sm font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
            value={exportCode}
            readOnly
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button onClick={handleCopy} className="w-full">
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t('common.copied', 'Copied!')}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    {t('controls.copyCode', 'Copy Code')}
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {copied ? t('common.copied', 'Copied!') : t('common.copy', 'Copy')}
            </TooltipContent>
          </Tooltip>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common.close', 'Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
